import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, List, Globe, Sparkles } from 'lucide-react';
import { appScreenshots, categories } from '../../data/mock';
import { PhoneMockup } from '../interactive/PhoneMockup';
import { useTranslation } from 'react-i18next';

export const DetailedFeatures = () => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="w-full">
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 px-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-600 font-semibold text-sm mb-6 border border-emerald-100">
            <Sparkles className="w-4 h-4" />
            <span>{t('detailedFeatures.tag')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            {t('detailedFeatures.title')}
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-12">
            {t('detailedFeatures.subtitle')}
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-12 max-w-5xl mx-auto">
            <div className="flex-shrink-0">
              <PhoneMockup currentScreen={appScreenshots.map} />
            </div>

            <div className="space-y-8 text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 text-lg">{t('detailedFeatures.f1_title')}</h4>
                  <p className="text-slate-500">{t('detailedFeatures.f1_desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <List className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 text-lg">{t('detailedFeatures.f2_title')}</h4>
                  <p className="text-slate-500">{t('detailedFeatures.f2_desc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <Globe className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 text-lg">{t('detailedFeatures.f3_title')}</h4>
                  <p className="text-slate-500">{t('detailedFeatures.f3_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 pt-16 border-t border-slate-100 px-6 max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              {t('detailedFeatures.categories_title')}
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('detailedFeatures.categories_subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -8, scale: 1.05 }}
                transition={{ delay: index * 0.05, type: "spring", stiffness: 150 }}
                className="bg-white rounded-3xl p-6 text-center shadow-md hover:shadow-xl transition-all border border-slate-100 flex flex-col items-center justify-center gap-4 group cursor-pointer"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-[#0DA2E7] group-hover:text-white transition-colors">
                  <img src={category.icon} alt={category.name} className="w-8 h-8 object-contain transition-transform group-hover:scale-110 group-hover:brightness-0 group-hover:invert" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 group-hover:text-[#0DA2E7] transition-colors">{category.name}</h4>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
